import { useEffect, useState } from "react";
import { auth, db } from "./firebase";
import { toast } from 'react-toastify';
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { onAuthStateChanged, signOut } from "firebase/auth";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore"; 


// --- GLOBAL UI CONTEXT & COMPONENTS ---
import { NotificationProvider } from "./context/NotificationContext";
import Navbar from "./components/Navbar";
import SyncNotification from "./components/SyncNotification";
import LoadingScreen from "./components/LoadingScreen";
import ErrorPage from "./pages/ErrorPage";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// --- PUBLIC & AUTH PAGES ---
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import QRRedirect from "./pages/QRRedirect";
import QRScanner from "./pages/QRScanner";
import Maintenance from './pages/Maintenance';

// --- CORE DASHBOARD PAGES ---
import Home from "./pages/Home";
import Projects from "./pages/Projects";
import ProjectDetails from "./pages/ProjectDetails";
import Workers from "./pages/Workers";
import Register from "./pages/Register";
import Customers from "./pages/Customers";
import LeaveRequests from "./pages/LeaveRequests";
import AdminApprovals from "./pages/AdminApprovals";
import Permits from "./pages/Permits";

// --- SAFETY HUB MENUS ---
import SafetyMenu from "./pages/SafetyMenu";
import SafetyLog from "./pages/SafetyLog";
import PolicySubMenu from "./pages/PolicySubMenu";

// --- UNIFIED SAFETY HUBS (Add/Edit/View/Print All-in-One) ---
import Checklists from "./pages/Checklists";
import IncidentReports from "./pages/IncidentReports";
import ToolboxTalks from "./pages/ToolboxTalks";
import Chemicals from "./pages/Chemicals";

// --- FIRST AID SUITE ---
import FirstAidMenu from "./pages/FirstAidMenu";
import FirstAidBoxes from "./pages/FirstAidBoxes";
import FirstAidTreatments from "./pages/FirstAidTreatments";

// --- PROCEDURES SUITE ---
import Procedures from "./pages/Procedures";
import ProcedureView from "./pages/ProcedureView";

// --- MULTI-STEP SAFETY DOCUMENTS (CRUD) ---
import HiraForm from "./pages/HiraForm";
import HiraLog from "./pages/HiraLog";
import HiraView from "./pages/HiraView";
import HiraEdit from "./pages/HiraEdit";

import IBRAForm from "./pages/IBRAForm";
import IBRALog from "./pages/IBRALog";
import IBRAView from "./pages/IBRAView";
import IBRAEdit from "./pages/IBRAEdit";

import HSEPlan from "./pages/HSEPlan";
import HSELog from "./pages/HSELog";
import HSEPlanView from "./pages/HSEPlanView";
import HSEPlanEdit from "./pages/HSEPlanEdit";

import FallProtection from "./pages/FallProtection";
import FallProtectionLog from "./pages/FallProtectionLog";
import FallProtectionForm from "./pages/FallProtectionForm";
import FallProtectionView from "./pages/FallProtectionView";
import FallProtectionEdit from "./pages/FallProtectionEdit";

// --- STATUTORY POLICIES & COMPONENTS ---
import EnvironmentalPlan from "./pages/EnvironmentalPlan";
import Structure from "./pages/Structure";
import Agreements from "./pages/Agreements";
import Appointments from "./pages/Appointments";
import Emergency from "./pages/Emergency";
import Registers from "./pages/Registers";
import Reports from "./pages/Reports";

// --- PROTECTED ROUTE WRAPPER ---
const ProtectedRoute = ({ user, children }) => {
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  return children;
};

// --- MAIN APP COMPONENT ---
function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

 

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const userDocRef = doc(db, "users", firebaseUser.uid);
          const snap = await getDoc(userDocRef);
          
          let role = "worker"; // Default fallback
          let userData = {};

          if (snap.exists()) {
            userData = snap.data();
            role = userData.role || "worker";
          } else {
            // Create default profile if missing
            userData = { email: firebaseUser.email, role: "worker", createdAt: serverTimestamp() };
            await setDoc(userDocRef, userData, { merge: true });
          }

          setUser({ 
            uid: firebaseUser.uid, 
            email: firebaseUser.email, 
            role: role,
            ...userData
          });

        } catch (err) {
          console.error("Auth fetch error:", err);
          setUser({ uid: firebaseUser.uid, email: firebaseUser.email, role: "worker" });
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);
 const isMaintenanceMode = true; 

  if (isMaintenanceMode) {
    return <Maintenance />;
  }
  const handleLogout = async () => {
    await signOut(auth);
    navigate("/login");
  };

  // --- BRANDED LOADING SCREEN ---
  if (loading) {
    return <LoadingScreen message="Authenticating Secure Session..." />;
  }

  return (
 
    // Wrap entire app in NotificationProvider for global toast alerts
    <NotificationProvider>
      <div className="App bg-light min-vh-100">
        
        {/* GLOBAL NAVBAR */}
        {user && <Navbar user={user} onLogout={handleLogout} />}
        
        {/* OFFLINE SYNC POPUP */}
        <SyncNotification />

        {/* ROUTING MAP */}
        <Routes>
          {/* --- PUBLIC ROUTES --- */}
          <Route path="/login" element={user ? <Navigate to="/" /> : <Login user={user} />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          
          {/* --- QR SCANNING --- */}
          <Route path="/scan/:id" element={<ProtectedRoute user={user}><QRRedirect user={user} /></ProtectedRoute>} />
          <Route path="/scan/:type/:id" element={<ProtectedRoute user={user}><QRRedirect user={user} /></ProtectedRoute>} />
          <Route path="/scanner" element={<ProtectedRoute user={user}><QRScanner user={user} /></ProtectedRoute>} />

          {/* --- CORE DASHBOARD ROUTES --- */}
          <Route path="/" element={<ProtectedRoute user={user}><Home user={user} /></ProtectedRoute>} />
          <Route path="/projects" element={<ProtectedRoute user={user}><Projects user={user} /></ProtectedRoute>} />
          <Route path="/projects/:id" element={<ProtectedRoute user={user}><ProjectDetails user={user} /></ProtectedRoute>} />
          <Route path="/workers" element={<ProtectedRoute user={user}><Workers user={user} /></ProtectedRoute>} />
          <Route path="/customers" element={<ProtectedRoute user={user}><Customers user={user} /></ProtectedRoute>} />
          <Route path="/leave" element={<ProtectedRoute user={user}><LeaveRequests user={user} /></ProtectedRoute>} />
          <Route path="/permits" element={<ProtectedRoute user={user}><Permits user={user} /></ProtectedRoute>} />
          
          <Route path="/admin/approvals" element={<ProtectedRoute user={user}><AdminApprovals user={user} /></ProtectedRoute>} />
          <Route path="/register" element={<ProtectedRoute user={user}><Register user={user} /></ProtectedRoute>} />

          {/* --- MAIN SAFETY HUB --- */}
          <Route path="/safety" element={<ProtectedRoute user={user}><SafetyMenu user={user} /></ProtectedRoute>} />
          <Route path="/safety/log" element={<ProtectedRoute user={user}><SafetyLog user={user} /></ProtectedRoute>} />
          <Route path="/safety/policies-menu" element={<ProtectedRoute user={user}><PolicySubMenu user={user} /></ProtectedRoute>} />

          {/* --- UNIFIED HUBS (Checklists, Incidents, Toolbox, Chemicals) --- */}
          <Route path="/checklists" element={<ProtectedRoute user={user}><Checklists user={user} /></ProtectedRoute>} />
          <Route path="/safety/incidents" element={<ProtectedRoute user={user}><IncidentReports user={user} /></ProtectedRoute>} />
          <Route path="/safety/toolbox" element={<ProtectedRoute user={user}><ToolboxTalks user={user} /></ProtectedRoute>} />
          <Route path="/safety/chemicals/log" element={<ProtectedRoute user={user}><Chemicals user={user} /></ProtectedRoute>} />

          {/* --- FIRST AID SUITE --- */}
          <Route path="/safety/first-aid" element={<ProtectedRoute user={user}><FirstAidMenu user={user} /></ProtectedRoute>} />
          <Route path="/safety/first-aid/boxes" element={<ProtectedRoute user={user}><FirstAidBoxes user={user} /></ProtectedRoute>} />
          <Route path="/safety/first-aid/treatments" element={<ProtectedRoute user={user}><FirstAidTreatments user={user} /></ProtectedRoute>} />

          {/* --- SWP & PROCEDURES --- */}
          <Route path="/safety/procedures" element={<ProtectedRoute user={user}><Procedures user={user} /></ProtectedRoute>} />
          

          {/* --- HIRA (Hazard Identification) --- */}
          <Route path="/safety/hiraew" element={<ProtectedRoute user={user}><HiraForm user={user} /></ProtectedRoute>} />
          <Route path="/safety/hira-log" element={<ProtectedRoute user={user}><HiraLog user={user} /></ProtectedRoute>} />
          <Route path="/safety/hira/view/:id" element={<ProtectedRoute user={user}><HiraView user={user} /></ProtectedRoute>} />
          <Route path="/safety/hira/edit/:id" element={<ProtectedRoute user={user}><HiraEdit user={user} /></ProtectedRoute>} />

          {/* --- IBRA (Issue Based) --- */}
          <Route path="/safety/ibraew" element={<ProtectedRoute user={user}><IBRAForm user={user} /></ProtectedRoute>} />
          <Route path="/safety/ibra-log" element={<ProtectedRoute user={user}><IBRALog user={user} /></ProtectedRoute>} />
          <Route path="/safety/ibra/view/:id" element={<ProtectedRoute user={user}><IBRAView user={user} /></ProtectedRoute>} />
          <Route path="/safety/ibra/edit/:id" element={<ProtectedRoute user={user}><IBRAEdit user={user} /></ProtectedRoute>} />

          {/* --- HSE PLAN --- */}
          <Route path="/safety/hse-plan" element={<ProtectedRoute user={user}><HSEPlan user={user} /></ProtectedRoute>} />
          <Route path="/safety/hse-log" element={<ProtectedRoute user={user}><HSELog user={user} /></ProtectedRoute>} />
          <Route path="/safety/hse-plan/view/:id" element={<ProtectedRoute user={user}><HSEPlanView user={user} /></ProtectedRoute>} />
          <Route path="/safety/hse-plan/edit/:id" element={<ProtectedRoute user={user}><HSEPlanEdit user={user} /></ProtectedRoute>} />

          {/* --- FALL PROTECTION --- */}
          <Route path="/safety/fall-protection" element={<ProtectedRoute user={user}><FallProtection user={user} /></ProtectedRoute>} />
          <Route path="/safety/fall-protection/log" element={<ProtectedRoute user={user}><FallProtectionLog user={user} /></ProtectedRoute>} />
          <Route path="/safety/fall-protectionew" element={<ProtectedRoute user={user}><FallProtectionForm user={user} /></ProtectedRoute>} />
          <Route path="/safety/fall-protection/view/:id" element={<ProtectedRoute user={user}><FallProtectionView user={user} /></ProtectedRoute>} />
          <Route path="/safety/fall-protection/edit/:id" element={<ProtectedRoute user={user}><FallProtectionEdit user={user} /></ProtectedRoute>} />

          {/* --- STATUTORY & MISC SAFETY --- */}
          <Route path="/safety/structure" element={<ProtectedRoute user={user}><Structure user={user} /></ProtectedRoute>} />
          <Route path="/safety/agreements" element={<ProtectedRoute user={user}><Agreements user={user} /></ProtectedRoute>} />
          <Route path="/safety/appointments" element={<ProtectedRoute user={user}><Appointments user={user} /></ProtectedRoute>} />
          <Route path="/safety/environmental-plan" element={<ProtectedRoute user={user}><EnvironmentalPlan user={user} /></ProtectedRoute>} />
          <Route path="/safety/emergency" element={<ProtectedRoute user={user}><Emergency user={user} /></ProtectedRoute>} />
          <Route path="/safety/registers" element={<ProtectedRoute user={user}><Registers user={user} /></ProtectedRoute>} />
          <Route path="/safety/reports" element={<ProtectedRoute user={user}><Reports user={user} /></ProtectedRoute>} />

          {/* --- CATCH ALL (404 ERROR PAGE) --- */}
          <Route path="*" element={<ErrorPage />} />
        </Routes>
        <ToastContainer position="top-right" autoClose={3000} theme="colored" />
      </div>
    </NotificationProvider>

  );
  
}

export default App;